import React from "react";

import { faEye } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Big from "big.js";
import { get } from "lodash";

import {
  ActionButton,
  ActionButtonContainer,
} from "components/AgGrid/ActionButton";
import { BusinessRelationshipDialog } from "components/Business/BusinessForm/Relationships/Form";
import { getDecimalPlacesAndStep } from "components/Form/FormikControls";

import { businessRelationsRateTypes } from "constants/businesses";

import { formatPercentage, getDollarPriceStringFromCents } from "lib/index";
import {
  openBusinessRelationshipHashRoute,
  openEditBusinessModal,
} from "lib/navigation";

import { useBoolean } from "hooks";

export const formatRelationshipRate = relationship => {
  if (relationship.rateType === businessRelationsRateTypes.PERCENTAGE) {
    const rateValuePercentage = new Big(relationship.rateValue).times(100);
    const { decimalPlacesToShow } = getDecimalPlacesAndStep(
      rateValuePercentage,
      2,
      4,
    );
    return `${formatPercentage(relationship.rateValue, { decimalPlaces: decimalPlacesToShow, roundingMethod: null })}`;
  } else if (relationship.rateType === businessRelationsRateTypes.HEAD) {
    return `$${getDollarPriceStringFromCents(relationship.rateValue)}/head`;
  } else {
    return null;
  }
};

export const formatRelationship = relationship => {
  const rateText = formatRelationshipRate(relationship);

  if (rateText) {
    return `${relationship.relationType} (${rateText})`;
  } else {
    return relationship.relationType;
  }
};

export const BusinessRelationshipRenderer = ({ node, colDef, data }) => {
  const value = get(data, colDef.field);

  if (node.group || !value) {
    return null;
  }

  return value.map(relationship => {
    return (
      <ActionButton
        key={relationship.id}
        onClick={() => openEditBusinessModal(relationship.relatedToId)}
      >
        {formatRelationship(relationship)}
      </ActionButton>
    );
  });
};

export const BusinessRelationshipActionRender = ({ data, context }) => {
  const [isOpen, handleOpen, handleClose] = useBoolean(false);

  if (!data) {
    return null;
  }

  const { saveRelationship, deleteRelationship, allowDelete, readOnly } =
    context;

  const handleSubmit = values => {
    saveRelationship(values);
    handleClose();
  };

  const { relationship } = data;

  const handleDelete = () => {
    deleteRelationship(relationship.id);
    handleClose();
  };

  return (
    <>
      <ActionButtonContainer>
        <ActionButton onClick={handleOpen} data-tour="editRelation">
          <FontAwesomeIcon icon={faEye} />
          &nbsp;View/Edit
        </ActionButton>
      </ActionButtonContainer>
      {isOpen && (
        <BusinessRelationshipDialog
          isOpen={isOpen}
          initialValues={relationship}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          handleDelete={handleDelete}
          readOnly={readOnly}
          allowDelete={allowDelete}
        />
      )}
    </>
  );
};

export const HashRouteBusinessRelationshipActionRender = ({ data, colDef }) => {
  if (!data) {
    return null;
  }

  const relationshipId = get(data, colDef.field);

  const handleOpen = () => {
    openBusinessRelationshipHashRoute(relationshipId);
  };

  return (
    <>
      <ActionButtonContainer>
        <ActionButton onClick={handleOpen} data-tour="editRelation">
          <FontAwesomeIcon icon={faEye} />
          &nbsp;View/Edit
        </ActionButton>
      </ActionButtonContainer>
    </>
  );
};
